import { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { ScopedCssBaseline, CircularProgress, Box } from '@mui/material';

import { ChatBotProvider } from './ChatBot';
import { ChatDrawer } from './ChatDrawer';
import { ChatFloatingButton } from './ChatFloatingButton';
import { WidgetAnnouncerProvider } from './WidgetAnnouncerProvider';
import {
  useFetchWidgetConfig,
  type WidgetConfig,
} from '../hooks/useFetchWidgetConfig';
import { FALLBACK_THEME, FALLBACK_AGENT_NAME } from '../utils/fallback';
import {
  createConfigWidgetTheme,
  createFallbackWidgetTheme,
} from './createWidgetTheme';
import { initWidgetAnalytics } from '../utils/analytics';
import { sessionIdStorageKey } from '../utils/sessionIdentity';
import { LauncherEmbedStyleProvider } from '../context/LauncherEmbedStyleContext';
import { WidgetI18nProvider, useWidgetI18n } from '../i18n/WidgetI18nProvider';
import { trackWidgetLanguageApplied } from '../i18n/track-language-applied';
import type { LauncherEmbedStyle } from '@/lib/embed-style';

function LoadingChatWidgetIndicator({
  launcherEmbedStyle,
}: {
  launcherEmbedStyle: LauncherEmbedStyle;
}) {
  const { t, locale } = useWidgetI18n();
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: `${launcherEmbedStyle.my}px`,
        right: `${launcherEmbedStyle.mx}px`,
        zIndex: launcherEmbedStyle.zIndex,
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: FALLBACK_THEME.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress
        size={24}
        sx={{ color: '#ffffff' }}
        lang={locale}
        aria-label={t('launcher.loadingWidget')}
      />
    </Box>
  );
}

interface ChatWidgetProps {
  engageId: string;
  /** Service unavailable message from data-service-unavailable-message attribute - used when API is completely down */
  serviceUnavailableMessage?: string;
  launcherEmbedStyle: LauncherEmbedStyle;
  /** Enabled via data-dock-mode on the embed script (desktop bar layout). */
  dockModeEnabled?: boolean;
}

// Fallback customization when config fails
const fallbackCustomization: WidgetConfig['customization'] = {
  agentName: FALLBACK_AGENT_NAME,
  widgetLauncherStyle: 'icon_with_name',
  chatButtonAccent: 'primary_only',
  chatHeight: 'full_height',
};

export function ChatWidget({
  engageId,
  serviceUnavailableMessage,
  launcherEmbedStyle,
  dockModeEnabled = false,
}: ChatWidgetProps) {
  const { config, loading, error } = useFetchWidgetConfig(engageId);

  // Initialize widget analytics once config has loaded (runtime token from chat-config API)
  useEffect(() => {
    if (!config?.analytics || config.serviceDown) return;
    const sessionId =
      localStorage.getItem(sessionIdStorageKey(engageId)) ?? undefined;
    initWidgetAnalytics({
      token: config.analytics.mixpanelToken,
      enabled: config.analytics.mixpanelEnabled,
      engageId,
      institutionName: config.customization?.institutionName,
      sessionId,
      launcherMx: launcherEmbedStyle.mx,
      launcherMy: launcherEmbedStyle.my,
      launcherZIndex: launcherEmbedStyle.zIndex,
    });
    trackWidgetLanguageApplied(engageId);
  }, [config, engageId, launcherEmbedStyle]);

  // Config fetch failed: keep the launcher and show the unavailable fallback.
  // Archived institutions set serviceDown and must not render the widget at all.
  const isConfigError = Boolean(error) && !config;

  // Use fallback theme when config fails, otherwise use config theme (with font scale from customization)
  const theme = config
    ? createConfigWidgetTheme(
        config.theme,
        config.customization?.chatFontSize ?? 'sm',
        config.widgetBrand
      )
    : createFallbackWidgetTheme();

  // Build customization with proper fallback priority:
  // 1. Backend API message (if config loaded and has message)
  // 2. Embed script message (data-service-unavailable-message attribute)
  // 3. Hardcoded fallback (handled in ServiceUnavailableContent)
  const customization: WidgetConfig['customization'] = config?.customization
    ? {
        ...config.customization,
        // If API message is empty/null, fall back to embed script message
        serviceUnavailableMessage:
          config.customization.serviceUnavailableMessage ||
          serviceUnavailableMessage,
      }
    : {
        // API failed entirely - use embed script message (or undefined for hardcoded fallback)
        ...fallbackCustomization,
        serviceUnavailableMessage: serviceUnavailableMessage,
      };

  if (config?.serviceDown) {
    return null;
  }

  // While loading, show loading indicator in a minimal floating button position
  if (loading) {
    return (
      <WidgetI18nProvider>
        <LoadingChatWidgetIndicator launcherEmbedStyle={launcherEmbedStyle} />
      </WidgetI18nProvider>
    );
  }

  return (
    <WidgetI18nProvider>
      <LauncherEmbedStyleProvider value={launcherEmbedStyle}>
        <ChatBotProvider
          engageId={engageId}
          customization={customization}
          captureMclidCookie={config?.features?.captureMclidCookie === true}
          mandatoryIntakeOnOpen={config?.features?.mandatoryIntakeOnOpen === true}
          viewerCountry={config?.viewerCountry ?? null}
          initialServiceDown={isConfigError}
          dockModeEnabled={dockModeEnabled}
        >
          <WidgetAnnouncerProvider>
            <ThemeProvider theme={theme}>
              <ScopedCssBaseline>
                {/* Always show floating button */}
                <ChatFloatingButton />

                {/* Chat drawer handles both normal and error states. The avatar
                    header + toggle render INSIDE the drawer when avatar mode is
                    on \u2014 see ChatDrawer.tsx. */}
                <ChatDrawer />
              </ScopedCssBaseline>
            </ThemeProvider>
          </WidgetAnnouncerProvider>
        </ChatBotProvider>
      </LauncherEmbedStyleProvider>
    </WidgetI18nProvider>
  );
}
